import { Injectable, TemplateRef, Type } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

/**
 * A panel inside the chat window (new conversation, details, notifications, ...), in place of a dialog over the page.
 * The ref has the part of MatDialogRef the chat's dialogs use, so they are opened here unchanged: the window provides
 * it as MatDialogRef, and their data as MAT_DIALOG_DATA.
 */
export class ChatSheetRef<R = unknown> {
  private readonly closed = new Subject<R | undefined>();

  constructor(private readonly onClose: (ref: ChatSheetRef<R>) => void) {}

  close(result?: R): void {
    if (this.closed.closed) return;
    this.onClose(this);
    this.closed.next(result);
    this.closed.complete();
  }

  afterClosed(): Observable<R | undefined> {
    return this.closed.asObservable();
  }
}

export interface ChatSheet {
  content: Type<unknown> | TemplateRef<unknown>;
  data: unknown;
  ref: ChatSheetRef<any>; // eslint-disable-line @typescript-eslint/no-explicit-any -- each panel types its own result
}

/** A question asked inside the chat window, in place of the app's confirmation dialog over the page. */
export interface ChatConfirm {
  title: string;
  message: string;
  confirmText: string;
  cancelText?: string;
  /** a destructive action (delete, remove, leave): the confirm button is red */
  danger?: boolean;
  /** a second way to go ahead, shown between Cancel and the confirm button (unlink only this one, or all of them) */
  secondText?: string;
  /** a list to tick, with Select all; the confirm button waits for one to be ticked (pick) */
  choices?: string[];
}

/** The one panel showing in the chat window, if any. Opening another closes it first, with no result. */
@Injectable({ providedIn: 'root' })
export class ChatSheetService {
  private readonly sheet = new BehaviorSubject<ChatSheet | null>(null);
  readonly sheet$ = this.sheet.asObservable();
  /** A question on top of everything, the open panel included (a panel may ask one), and how to answer it. */
  private readonly question = new BehaviorSubject<ChatConfirm | null>(null);
  readonly confirm$ = this.question.asObservable();
  private resolveQuestion: ((answer: 'confirm' | 'second' | null, picked: number[]) => void) | null = null;
  /** what is ticked in the open question's `choices` */
  private picked = new Set<number>();

  get currentConfirm(): ChatConfirm | null {
    return this.question.value;
  }

  /** Asks over the open panel without closing it; answers false if dismissed, replaced, or the window closes. */
  confirm(question: ChatConfirm): Promise<boolean> {
    return this.ask(question).then((a) => a === 'confirm');
  }

  /** Like confirm, for a question with `secondText`: which button was chosen, null if dismissed. */
  ask(question: ChatConfirm): Promise<'confirm' | 'second' | null> {
    return this.openQuestion(question).then(([answer]) => answer);
  }

  /** For a question with `choices`: the ticked ones (indexes, in order), null if dismissed. */
  pick(question: ChatConfirm): Promise<number[] | null> {
    return this.openQuestion(question).then(([answer, picked]) => (answer === 'confirm' ? picked : null));
  }

  get pickedCount(): number {
    return this.picked.size;
  }

  get allPicked(): boolean {
    const n = this.question.value?.choices?.length ?? 0;
    return n > 0 && this.picked.size === n;
  }

  isPicked(i: number): boolean {
    return this.picked.has(i);
  }

  togglePick(i: number): void {
    if (!this.picked.delete(i)) this.picked.add(i);
  }

  toggleAllPicks(): void {
    if (this.allPicked) this.picked.clear();
    else (this.question.value?.choices ?? []).forEach((_c, i) => this.picked.add(i));
  }

  private openQuestion(question: ChatConfirm): Promise<['confirm' | 'second' | null, number[]]> {
    this.answer(false);
    this.picked = new Set();
    return new Promise((resolve) => {
      this.resolveQuestion = (answer, picked) => resolve([answer, picked]);
      this.question.next(question);
    });
  }

  answer(yes: boolean): void {
    this.settle(yes ? 'confirm' : null);
  }

  answerSecond(): void {
    this.settle('second');
  }

  private settle(answer: 'confirm' | 'second' | null): void {
    const resolve = this.resolveQuestion;
    const picked = [...this.picked].sort((a, b) => a - b);
    this.resolveQuestion = null;
    this.question.next(null);
    resolve?.(answer, picked);
  }

  get current(): ChatSheet | null {
    return this.sheet.value;
  }

  open<R = unknown>(content: Type<unknown> | TemplateRef<unknown>, options: { data?: unknown } = {}): ChatSheetRef<R> {
    this.closeAll();
    const ref = new ChatSheetRef<R>((closing) => {
      // A panel already replaced by another must not take that one down with it.
      if (this.sheet.value?.ref === closing) this.sheet.next(null);
    });
    this.sheet.next({ content, data: options.data, ref });
    return ref;
  }

  closeAll(): void {
    this.answer(false);
    this.sheet.value?.ref.close();
  }
}
